import { useCallback } from 'react';
import PropTypes from 'prop-types';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { ascend, descend, isEmpty, prop, propOr, sortWith } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import SectionHeader from 'components/atoms/GefMui/SectionHeader';
import TableHasNoResults from 'components/atoms/GefMui/TableHasNoResults';
import { useIgnoreErrorsWhen } from 'components/molecules/ComponentLoader/hooks';
import fetch from 'features/common/fetch';
import { TASK_TYPES } from 'features/views/taskDetail/constants';
import { getDetail } from 'features/views/taskDetail/selectors';
import { equalOrThreeDashes, mailToLink, phoneToLink } from 'utils';

const { urls } = fetch.constants;

const { LOSE_COMISSION } = TASK_TYPES;

export const PolicyAdminsTable = ({ onReload }) => {
	const taskDetail = useSelector(getDetail);

	const { policy = {}, metadata = {}, taskTypeId } = taskDetail;
	const { policyAdmins = [] } = policy;
	const { common = {} } = metadata;

	const isLoseComissionType = taskTypeId === LOSE_COMISSION;
	const sortByOrderNrAndSellerIn = sortWith([ascend(prop('orderNr')), descend(propOr(0, 'sellerIn'))]);

	let admins = sortByOrderNrAndSellerIn(policyAdmins);

	// if task is LOSE_COMISSION type > take data from metadata
	if (isLoseComissionType) {
		admins = [
			{
				sellerIn: common.policyAgentSellerIn,
				fullName: common.policyAgentName,
				phone: common.policyAgentPhone,
				email: common.policyAgentEmail,
				login: common.policyAgentLogin,
			},
		];
	}

	const hasRecords = !isEmpty(admins);

	const generateRole = useCallback(
		(sellerIn) => {
			if (isLoseComissionType) {
				return getLabelMessage('detail:policyAdmin');
			} else if (!isNilOrEmpty(sellerIn)) {
				return getLabelMessage('detail:policyAdmin');
			} else {
				return getLabelMessage('detail:subseller');
			}
		},
		[isLoseComissionType],
	);

	const ignoreErrorsForTaskDetailUserHasNoAccess = useIgnoreErrorsWhen({
		fetchStorePath: urls.taskDetail,
		attributePath: ['metaData', 'parsedContent', 'status'],
		value: 403,
	});

	return (
		<ComponentLoader
			onReload={onReload}
			fetchStorePath={urls.taskDetail}
			ignoreErrors={ignoreErrorsForTaskDetailUserHasNoAccess}
		>
			<Paper sx={{ p: 3, borderRadius: 2 }}>
				<Stack spacing={2}>
					<SectionHeader text={getLabelMessage('detail:tableSubheader')} />
					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>{getLabelMessage('detail:tableHeadOrderNr')}</TableCell>
									<TableCell>{getLabelMessage('detail:tableHeadReachedNumber')}</TableCell>
									<TableCell>{getLabelMessage('detail:tableHeadLogin')}</TableCell>
									<TableCell>{getLabelMessage('detail:tableHeadName')}</TableCell>
									<TableCell>{getLabelMessage('detail:tableHeadPhone')}</TableCell>
									<TableCell>{getLabelMessage('detail:tableHeadEmail')}</TableCell>
									<TableCell>{getLabelMessage('detail:tableHeadRole')}</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{!hasRecords ? (
									<TableHasNoResults />
								) : (
									admins.map((item, i) => (
										<TableRow key={i}>
											<TableCell>
												{item.orderNr === 0
													? equalOrThreeDashes()
													: equalOrThreeDashes(item.orderNr)}
											</TableCell>
											<TableCell>{equalOrThreeDashes(item.sellerIn)}</TableCell>
											<TableCell>{equalOrThreeDashes(item.login)}</TableCell>
											<TableCell>
												{item.fullName
													? item.fullName
													: item.surname
													  ? `${item.surname} ${item.firstName || ''}`
													  : `${getLabelMessage('detail:inactivePolicyAdmin')}`}
											</TableCell>
											<TableCell>{phoneToLink(item.phone, true)}</TableCell>
											<TableCell>{mailToLink(item.email, true)}</TableCell>
											<TableCell>{generateRole(item.sellerIn)}</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</Stack>
			</Paper>
		</ComponentLoader>
	);
};

PolicyAdminsTable.propTypes = {
	onReload: PropTypes.func,
};
export default PolicyAdminsTable;
